<template>
  <div v-if="$can('view', 'YachtModel')">
    <v-row
      no-gutters
      class="mb-6"
    >
      <v-spacer />
      <v-col cols="auto">
        <v-btn
          color="primary"
          @click="equipmentPackDialog.equipmentPack = undefined; equipmentPackDialog.visible = true"
        >
          <v-icon
            left
            small
          >
            fa fa-plus
          </v-icon>
          Add new pack
        </v-btn>
      </v-col>
    </v-row>
    <div v-if="yardEquipmentPacks.length === 0">
      <v-card
        color="grey lighten-2"
        outlined
        class="text-center pa-8"
      >
        <div class="subtitle-1 grey--text text--darken-1">
          There are no yard packs yet
        </div>
        <div class="mt-4">
          <v-btn
            color="grey darken-2"
            outlined
            @click="equipmentPackDialog.equipmentPack = undefined; equipmentPackDialog.visible = true"
          >
            <v-icon
              left
              small
            >
              fa fa-plus
            </v-icon>
            Add new pack
          </v-btn>
        </div>
      </v-card>
    </div>
    <div
      v-for="pack in yardEquipmentPacks"
      :key="pack.id"
      class="mb-6"
    >
      <v-card
        class="fill-height"
        :ripple="false"
        :style="[ pack.status === equipmentPackStatusDiscarded ? { opacity: 0.4 } : {}]"
      >
        <v-card-title
          class="body-2 px-4 py-2 no-gutters clickable"
          @click="pack.is_expanded = !pack.is_expanded"
        >
          <v-col>
            <div class="font-weight-strong subtitle-1">
              {{ pack.label }}
            </div>
            <div class="caption grey--text">
              <v-chip
                label
                small
                :color="pack.status === equipmentPackStatusActive ? 'success' : 'error'"
                dark
                class="font-weight-bold"
              >
                {{ equipmentPackStatuses[pack.status].label }}
              </v-chip>
              <span class="ml-2">
                <flag
                  v-for="country in yachtModel.countries"
                  :key="pack.id + country.id"
                  :iso="country.code"
                  :title="country.name"
                  :squared="false"
                  class="elevation-5 ml-2"
                  :style="[ ...!pack.countries.some(c => c.id === country.id) && { opacity: 0.2, filter: 'grayscale(100%)' } ]"
                />
              </span>
            </div>
          </v-col>
          <v-col
            v-if="pack.updating"
            cols="auto"
            class="grey--text caption pl-3"
          >
            <v-progress-circular
              small
              :size="22"
              :width="1"
              indeterminate
              color="grey"
            />
          </v-col>
          <v-col
            cols="auto"
            class="pl-3"
          >
            <v-switch
              v-model="pack.default"
              dense
              label="Added by default"
              hide-details
              class="pa-0 ma-0"
              @click.stop.prevent="updatePack(pack, { yacht_models: [ { yacht_model_id: yachtModel.id, default: !pack.default } ] })"
            />
          </v-col>
          <v-col
            cols="auto"
            class="pl-3"
          >
            <v-switch
              v-model="pack.is_configurable"
              dense
              label="Items configurable"
              hide-details
              class="pa-0 ma-0"
              @click.stop.prevent="updatePack(pack, { is_configurable: !pack.is_configurable })"
            />
          </v-col>
          <v-col
            v-if="$can('view-purchase-price', 'YachtEquipmentPack')"
            cols="auto"
            class="pl-3"
          >
            <span class="grey--text">Purchase</span> {{ pack.purchase_price | currency(pack.currency, 0) }}
          </v-col>
          <v-col
            v-if="$can('view-sale-price', 'YachtEquipmentPack')"
            cols="auto"
            class="pl-3"
          >
            <span class="grey--text">Sale</span> <strong>{{ pack.sale_price | currency(pack.currency, 0) }}</strong>
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="grey darken-2"
              class="text-none"
              text
              @click="equipmentPackDialog.equipmentPack = pack; equipmentPackDialog.visible = true"
            >
              <v-icon
                left
                x-small
              >
                fa fa-pen
              </v-icon>
              Edit
            </v-btn>
          </v-col>
          <v-col
            cols="auto"
            class="pl-4"
          >
            <v-icon @click.stop="pack.is_expanded = !pack.is_expanded">
              {{ pack.is_expanded ? 'expand_less' : 'expand_more' }}
            </v-icon>
          </v-col>
        </v-card-title>
        <v-expand-transition>
          <div v-if="pack.is_expanded">
            <v-divider />
            <v-card-text>
              <div
                v-for="(item, index) in pack.items"
                :key="pack.id + '_' + index"
                class="caption list--striped pa-1"
              >
                {{ item.label }}
              </div>
            </v-card-text>
          </div>
        </v-expand-transition>
      </v-card>
    </div>

    <yacht-equipment-pack-edit-dialog
      :visible.sync="equipmentPackDialog.visible"
      :yacht-equipment-pack="equipmentPackDialog.equipmentPack"
      yard-equipment
      :yacht-model="yachtModel"
      @yachtEquipmentPack:updated="$emit('update')"
      @yachtEquipmentPack:created="$emit('update')"
    />
  </div>
</template>

<script>
import { sortBy } from 'lodash'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'
import YachtEquipmentPackEditDialog from '@/components/settings/yachtEquipmentPacks/YachtEquipmentPackEditDialog.vue'

export default {
  name: 'YachtModelDetailsYardEquipment',
  components: { YachtEquipmentPackEditDialog },
  props: {
    yachtModel: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      yardEquipmentPacks: [],
      equipmentPackDialog: {
        visible: false,
        equipmentPack: undefined
      }
    }
  },
  computed: {
    countries() {
      return sortBy(this.$store.getters['settings/charteringCountries'].data, 'name').map(c => {
        let yachtModelCountry = this.yachtModel.countries.find(pc => c.id === pc.id)

        return {
          ...c,
          included: !!yachtModelCountry,
          yachtModel: yachtModelCountry
        }
      })
    }
  },
  watch: {
    yachtModel() {
      this.loadEquipmentPacks()
    }
  },
  created() {
    this.loadEquipmentPacks()

    this.equipmentPackStatusActive = YachtEquipmentPack.STATUS_ACTIVE
    this.equipmentPackStatusDiscarded = YachtEquipmentPack.STATUS_DISCARDED
    this.equipmentPackStatusDraft = YachtEquipmentPack.STATUS_DRAFT
    this.equipmentPackStatuses = YachtEquipmentPack.statusData
  },
  methods: {
    loadEquipmentPacks() {
      this.yardEquipmentPacks = this.yachtModel.equipment_packs.filter(ep => ep.installed_by === YachtEquipmentPack.INSTALLED_BY_SHIPYARD).map(ep => {
        return {
          ...ep,
          is_expanded: ep.is_configurable || ep.default,
          updating: false,
          updated: false
        }
      }).sort((a, b) => {
        if (a.status === YachtEquipmentPack.STATUS_ACTIVE) return -1

        if (a.status === YachtEquipmentPack.STATUS_DRAFT && b.status === YachtEquipmentPack.STATUS_DISCARDED) return -1

        return 1
      })
    },
    async updatePack(pack, data) {
      pack.updating = true

      try {
        let payload = { ...pack }

        payload.countries = pack.countries.map(c => c.id)
        payload.yacht_models = [ { yacht_model_id: this.yachtModel.id, default: pack.default } ]

        payload = { ...payload, data }

        await YachtEquipmentPack.api.save(payload, pack.id)

        this.$emit('update')
      } catch (e) {
        this.$store.dispatch('alert', {
          color: 'error',
          text: 'There was an error. Equipment pack was not updated!'
        })
      } finally {
        pack.updating = false
      }
    }
  }
}
</script>
